/**
 * Calculator State Serializer
 * Utilities for exporting and importing calculator configurations
 */

import { z } from 'zod';

// Export format schema
const CalculatorExportSchema = z.object({
  version: z.string(),
  exportedAt: z.string(),
  calculatorType: z.enum(['reverse-piti', 'forward', 'buying-power']),
  state: z.record(z.any()),
  metadata: z.object({
    name: z.string().optional(),
    description: z.string().optional(),
  }).optional(),
});

export type CalculatorExport = z.infer<typeof CalculatorExportSchema>;

// Reverse PITI state schema for validation
const ReversePitiStateSchema = z.object({
  loanType: z.enum(['conventional', 'fha', 'va', 'usda']),
  vaStatus: z.enum(['activeDuty', 'separatedRetired']).nullable().optional(),
  vaDisabilityStatus: z.enum(['none', 'partial', 'full']).nullable().optional(),
  vaDisabilityRatingPercentage: z.number().min(10).max(90).optional(),
  desiredPiti: z.number().min(0),
  interestRate: z.number().min(0).max(20),
  loanTerm: z.number().min(1).max(50),
  downPaymentPercent: z.number().min(0).max(100),
  propertyTaxRate: z.number().min(0).max(10),
  annualInsurance: z.number().min(0),
  hoaFees: z.number().min(0),
  pmiRate: z.number().min(0).max(5),
  includePmi: z.boolean(),
});

export type ReversePitiExportState = z.infer<typeof ReversePitiStateSchema>;

/**
 * Serialize calculator state to exportable format
 */
export function serializeCalculatorState(
  state: Record<string, any>,
  calculatorType: CalculatorExport['calculatorType'],
  metadata?: CalculatorExport['metadata']
): CalculatorExport {
  return {
    version: '1.0',
    exportedAt: new Date().toISOString(),
    calculatorType,
    state,
    metadata,
  };
}

/**
 * Deserialize and validate imported calculator state
 */
export function deserializeCalculatorState(
  json: string
): { success: true; data: CalculatorExport } | { success: false; error: string } {
  try {
    const parsed = JSON.parse(json);
    const result = CalculatorExportSchema.safeParse(parsed);
    
    if (!result.success) {
      return {
        success: false,
        error: `Invalid format: ${result.error.errors.map(e => e.message).join(', ')}`,
      };
    }
    
    return { success: true, data: result.data };
  } catch (e) {
    return {
      success: false,
      error: 'Failed to parse JSON file',
    };
  }
}

/**
 * Validate Reverse PITI state values
 */
export function validateReversePitiState(
  state: Record<string, any>
): { success: true; data: ReversePitiExportState } | { success: false; error: string } {
  const result = ReversePitiStateSchema.safeParse(state);
  
  if (!result.success) {
    return {
      success: false,
      error: `Invalid state values: ${result.error.errors.map(e => `${e.path.join('.')}: ${e.message}`).join(', ')}`,
    };
  }
  
  return { success: true, data: result.data };
}

/**
 * Generate a file name for export
 */
export function generateExportFileName(
  calculatorType: string,
  metadata?: { name?: string }
): string {
  const date = new Date().toISOString().split('T')[0];
  const name = metadata?.name?.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase() || calculatorType;
  return `${name}-${date}.json`;
}

/**
 * Download JSON as file
 */
export function downloadAsJson(data: CalculatorExport, fileName: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Read file as text
 */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}
